import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { flareAPI, ccsAPI } from '../services/api';
import { read, clear, classifyFailure, REFUSED, MODE_CLAIM } from '../utils/flarePending';
import { markDone, PROGRESS_KEYS } from '../utils/learnProgress';

/**
 * Posts a first challenge that was completed before registration, once an account exists.
 *
 * Mounted once, app-wide, renders nothing. It watches for authentication appearing and
 * flushes whatever `flarePending` is holding.
 *
 * WHY HERE rather than in RegisterPage or AuthContext:
 *
 *   RegisterPage would miss the learner who takes the "I already have an account" door and
 *   signs in instead — the same held session, a different page. Both routes end with an
 *   authenticated session, which is the actual trigger.
 *
 *   AuthContext would work, but it is the most load-bearing file in the front end and this
 *   is a narrow, temporary concern. A component that renders null can be deleted in one line
 *   when the entry flow changes again.
 *
 * ── REWRITTEN 2026-09-18 (KAN-491) ────────────────────────────────────────────────────────
 *
 * Two defects here, separate from the storage bug in `flarePending`:
 *
 *   1. `markDone` ran BEFORE the post. Local progress therefore recorded the challenge as
 *      done whether or not the server ever received it — so the one place that might have
 *      revealed the loss instead asserted success. The dashboard falls back to local progress
 *      when the server read fails, which means a lost session could have rendered as a
 *      completed one.
 *
 *   2. The post was fire-and-forget with an empty catch, and `take()` had already cleared the
 *      payload. A failure destroyed the data and reported nothing, anywhere. Every learner
 *      who hit it looked identical to a learner who skipped the challenge.
 *
 * Now: read (without clearing) → post → clear and mark done ONLY on success. A refusal drops
 * the hold because retrying cannot change a 4xx; anything else leaves it for the next load,
 * bounded by the hold's TTL. Failures emit telemetry, because "nothing was held" and "the
 * write failed" have been indistinguishable in production and that is how this went unseen.
 *
 * Still fire-and-forget toward the LEARNER: nothing here surfaces mid-flow. They have already
 * seen their readout, and a capture problem is ours, not theirs.
 */
export default function FlarePendingFlush() {
  const { isAuthenticated } = useAuth();
  const flushed = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || flushed.current) return;
    flushed.current = true;

    const held = read();
    if (!held) return;

    const { mode, payload } = held;

    // The session id travels with the payload in both modes. Reusing it rather than minting
    // a new one keeps the KAN-279 store's idempotency meaningful: if this ever runs twice,
    // the second POST returns the stored row unchanged instead of writing a duplicate.
    const flareSessionId = payload && payload.flare_session_id;
    if (!flareSessionId) {
      clear();
      return;
    }

    // CLAIM: the session is already on the server because the learner asserted 13+ at the
    // doors. Only the ID was held, so this works from a browser that never saw the payload —
    // which is the whole point, and the case POST mode cannot reach.
    const request = mode === MODE_CLAIM
      ? flareAPI.linkSession(flareSessionId)
      : flareAPI.createSession(payload);

    request
      .then(() => {
        // Only now is it true. The server has the session; local progress may say so.
        clear();
        markDone(undefined, PROGRESS_KEYS.flare, { sessionId: flareSessionId });
      })
      .catch((error) => {
        const disposition = classifyFailure(error);
        if (disposition === REFUSED) clear();

        // Make the loss visible. This is the signal that did not exist on 2026-09-18, when a
        // real registration dropped its session and nothing anywhere recorded that it had.
        try {
          ccsAPI.telemetry({
            event_type: 'flare_pending_flush_failed',
            payload: {
              flare_session_id: flareSessionId,
              status: (error && error.response && error.response.status) || null,
              disposition,
              mode,
            },
          }).catch(() => {});
        } catch {
          /* telemetry must never be the thing that breaks a registration */
        }
      });
  }, [isAuthenticated]);

  return null;
}
